import React, { useState } from "react";
import {
  PlusIcon,
  MinusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/16/solid";
import { ComponentI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Input from "../../../../componentLibrary/Input";
import Button from "../../../../componentLibrary/Button";
import { CopyVariableButton } from "../../utils/CopyVariableButton";
import MultiSelectDropdown from "../../../../componentLibrary/MultiSelectDropdown";

const ArrowButtons = ({
  index,
  disabledUp = false,
  disabledDown = false,
  handleExchange,
}: {
  index: number;
  disabledUp?: boolean;
  disabledDown?: boolean;
  handleExchange: (index1: number, index2: number) => void;
}) => {
  return (
    <div className="flex flex-row gap-2">
      <Button
        variant="dark"
        size="sm"
        disabled={disabledUp}
        onClick={() => handleExchange(index, index - 1)}
      >
        <ArrowUpIcon className="w-4 h-4" />
      </Button>
      <Button
        variant="dark"
        size="sm"
        disabled={disabledDown}
        onClick={() => handleExchange(index, index + 1)}
      >
        <ArrowDownIcon className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function PriorityListContentCard({
  component,
  handleContentChange,
  variableNames,
}: {
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: string | boolean | Record<string, string> | string[],
  ) => void;
  variableNames: string[];
}) {
  const [choices, setChoices] = useState<string[]>(
    Object.keys(component.content)
      .filter((key) => key.startsWith("choice_"))
      .map((key) => component.content[key]),
  );

  const handleAddChoice = () => {
    const nextIndex = choices.length + 1;
    handleContentChange(component._id, `choice_${nextIndex}`, "");
    setChoices([...choices, ""]);
  };

  const handleRemoveLastChoice = () => {
    if (choices.length > 0) {
      const updatedChoices = choices.slice(0, -1);
      handleContentChange(component._id, `choice_${choices.length}`, undefined); // What would I do if I wanted to remove the key completely
      setChoices(updatedChoices);
    }
  };

  const linkedVariableOptions = variableNames
    .filter((name) => component.content.variable !== name)
    .map((name) => ({
      value: name,
      label: name,
    }));

  const exchangeChoices = (index1: number, index2: number) => {
    const newChoices = [...choices];
    const temp = newChoices[index1];
    newChoices[index1] = newChoices[index2];
    newChoices[index2] = temp;
    setChoices(newChoices);
    handleContentChange(
      component._id,
      `choice_${index1 + 1}`,
      newChoices[index1],
    );
    handleContentChange(
      component._id,
      `choice_${index2 + 1}`,
      newChoices[index2],
    );
  };

  return (
    <Card>
      <div className="space-y-3">
        <Input
          value={component.content.label}
          onChange={(e) =>
            handleContentChange(component._id, "label", e.target.value)
          }
          label="Label"
          placeholder="Choose a kind phrase to tell your partner."
          required
        />
        {choices.map((choice, index) => (
          <div key={`choice_${index + 1}`}>
            <Input
              value={choice}
              onChange={(e) => {
                const newChoiceValue = e.target.value;
                handleContentChange(
                  component._id,
                  `choice_${index + 1}`,
                  newChoiceValue,
                );
                const updatedChoices = [...choices];
                updatedChoices[index] = newChoiceValue;
                setChoices(updatedChoices);
              }}
              label={`Choice ${index + 1}`}
              placeholder={`Option ${index + 1}`}
              required
              TopRightElement={
                <ArrowButtons
                  index={index}
                  disabledUp={index === 0}
                  disabledDown={index === choices.length - 1}
                  handleExchange={exchangeChoices}
                />
              }
            />
          </div>
        ))}

        <div className="flex space-x-2 justify-end">
          <Button
            variant="default"
            size="sm"
            onClick={handleRemoveLastChoice}
            className="mb-1"
            disabled={choices.length < 1}
          >
            <MinusIcon className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={handleAddChoice} className="mb-1">
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>
        <div>
          <div className="text-md font-bold mb-1">
            Populate Choices from Variables
          </div>
          <MultiSelectDropdown
            options={linkedVariableOptions}
            value={
              component.content.linkedVariable?.map((name) => ({
                value: name,
                label: name,
              })) ?? []
            }
            onChange={(value) =>
              handleContentChange(
                component._id,
                "linkedVariable",
                value.map((option) => option.value),
              )
            }
          />
        </div>
        <Input
          value={component.content.variable}
          onChange={(e) =>
            handleContentChange(component._id, "variable", e.target.value)
          }
          label="Variable Name"
          placeholder="support_partner"
          required
          TopRightElement={
            <CopyVariableButton type="icon" component={component} />
          }
        />
      </div>
    </Card>
  );
}
